import React, { useState, useEffect } from "react";
import { Card } from "primereact/card";
import { InputText } from "primereact/inputtext";
import { InputSwitch } from "primereact/inputswitch";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Badge } from "primereact/badge";

export default function SlotConfigCard({ slot, products, onUpdate, onAssignProducts, onRemoveProduct }) {
  const [formData, setFormData] = useState({
    name: slot.name || '',
    is_enabled: Boolean(slot.is_enabled),
  });
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProductIds, setSelectedProductIds] = useState([]);

  useEffect(() => {
    setFormData({
      name: slot.name || '',
      is_enabled: Boolean(slot.is_enabled),
    });
    setHasChanges(false);
  }, [slot]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    const success = await onUpdate(slot.id, formData);
    if (success) {
      setHasChanges(false);
    }
    setIsSaving(false);
  };

  const handleAssignProducts = async () => {
    if (selectedProductIds.length > 0) {
      await onAssignProducts(slot.id, selectedProductIds);
      setSelectedProductIds([]);
      setShowProductDialog(false);
      setSearchTerm('');
    }
  };

  const handleRemoveProduct = async (productId) => {
    await onRemoveProduct(slot.id, productId);
  };

  // Filter products based on search term
  const filteredProducts = products?.filter(product =>
    product?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product?.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product?.category?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  // Get currently assigned product IDs
  const assignedProductIds = slot.products?.map(p => p.id) || [];

  // Filter out already assigned products from the selection dialog
  const availableProducts = filteredProducts.filter(p => !assignedProductIds.includes(p.id));

  const assignedProductsCount = slot.products?.length || 0;

  return (
    <>
      <Card className="shadow-md hover:shadow-lg transition-shadow">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <i className="pi pi-bars text-gray-400 cursor-move"></i>
              <h3 className="text-lg font-semibold text-gray-800">
                Slot {slot.position}
              </h3>
              {assignedProductsCount > 0 && (
                <Badge value={assignedProductsCount} severity="info" />
              )}
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Enabled</label>
              <InputSwitch
                checked={formData.is_enabled}
                onChange={(e) => handleInputChange('is_enabled', e.value)}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Slot Name
            </label>
            <InputText
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="Enter slot name"
              className="w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Assigned Products ({assignedProductsCount})
            </label>
            <Button
              label="Manage Products"
              icon="pi pi-plus"
              className="p-primary-btn w-full"
              onClick={() => setShowProductDialog(true)}
            />
          </div>

          {/* Display assigned products */}
          {slot.products && slot.products.length > 0 && (
            <div className="max-h-32 overflow-y-auto border border-gray-200 rounded p-2">
              {slot.products.map(product => (
                <div key={product.id} className="flex items-center justify-between py-1 px-2 bg-gray-50 rounded mb-1">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-800 truncate">
                      {product.sku}: {product.title}
                    </div>
                    <div className="text-xs text-gray-600">
                      Rs{product.price}
                      {product.discount > 0 && (
                        <span className="ml-2 text-green-600">
                          (-{product.discount}{product.discount_type === 'percentage' ? '%' : 'Rs'})
                        </span>
                      )}
                    </div>
                  </div>
                  <Button
                    icon="pi pi-times"
                    className="p-button-text p-button-danger p-button-sm"
                    onClick={() => handleRemoveProduct(product.id)}
                    tooltip="Remove product"
                  />
                </div>
              ))}
            </div>
          )}

          <div className="pt-2">
            <Button
              label="Save Changes"
              icon="pi pi-save"
              className="p-primary-btn w-full"
              onClick={handleSave}
              loading={isSaving}
              disabled={!hasChanges || isSaving}
            />
          </div>
        </div>
      </Card>

      {/* Product Selection Dialog */}
      <Dialog
        header={`Select Products for Slot ${slot.position}`}
        visible={showProductDialog}
        onHide={() => {
          setShowProductDialog(false);
          setSearchTerm('');
          setSelectedProductIds([]);
        }}
        style={{ width: '600px' }}
        footer={
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              {selectedProductIds.length} products selected
            </div>
            <div className="flex gap-2">
              <Button
                label="Cancel"
                icon="pi pi-times"
                className="p-button-text"
                onClick={() => {
                  setShowProductDialog(false);
                  setSearchTerm('');
                  setSelectedProductIds([]);
                }}
              />
              <Button
                label="Assign Products"
                icon="pi pi-check"
                className="p-primary-btn"
                onClick={handleAssignProducts}
                disabled={selectedProductIds.length === 0}
              />
            </div>
          </div>
        }
      >
        <div className="space-y-4">
          {/* Search */}
          <div>
            <InputText
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search products by name, SKU, or category..."
              className="w-full"
            />
          </div>

          {/* Select All */}
          {availableProducts.length > 0 && (
            <div className="flex items-center gap-2">
              <Checkbox
                checked={selectedProductIds.length === availableProducts.length && availableProducts.length > 0}
                onChange={(e) => {
                  if (e.checked) {
                    setSelectedProductIds(availableProducts.map(p => p.id));
                  } else {
                    setSelectedProductIds([]);
                  }
                }}
              />
              <label className="text-sm font-medium text-gray-700">
                Select All ({availableProducts.length} products)
              </label>
            </div>
          )}

          {/* Products List */}
          <div className="max-h-96 overflow-y-auto border border-gray-200 rounded">
            {availableProducts.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {searchTerm ? 'No products found matching your search.' : 'No products available.'}
              </div>
            ) : (
              availableProducts.map(product => (
                <div
                  key={product.id}
                  className="flex items-center gap-3 p-3 border-b border-gray-100 hover:bg-gray-50"
                >
                  <Checkbox
                    checked={selectedProductIds.includes(product.id)}
                    onChange={(e) => {
                      if (e.checked) {
                        setSelectedProductIds(prev => [...prev, product.id]);
                      } else {
                        setSelectedProductIds(prev => prev.filter(id => id !== product.id));
                      }
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-800">
                      {product.sku}: {product.title}
                    </div>
                    <div className="text-xs text-gray-600 flex gap-4">
                      <span>Rs{product.price}</span>
                      {product.discount > 0 && (
                        <span className="text-green-600">
                          (-{product.discount}{product.discount_type === 'percentage' ? '%' : 'Rs'})
                        </span>
                      )}
                      <span>Stock: {product.stock}</span>
                      {product.category && <span>{product.category}</span>}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </Dialog>
    </>
  );
}